package dev.danko.roblox_craft_ai.uikit.components

import dev.danko.roblox_craft_ai.uikit.i18n.strings
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.dom.ref
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import dev.danko.roblox_craft_ai.uikit.theme.LocalAppTheme
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.HTMLDivElement
import kotlin.js.Date

private fun nowMsEd(): Long = Date.now().toLong()
private fun toBase36(num: Long): String {
    return js("num.toString(36)") as String
}

@Composable
fun CodeEditor(
    initialCode: String = "",
    onCodeChange: (String) -> Unit = {},
    modifier: Modifier = Modifier,
    tabSize: Int = 2,
    readOnly: Boolean = false,
    minHeight: CSSSizeValue<CSSUnit.px> = 380.px,
    maxHeight: CSSSizeValue<CSSUnit.px> = 560.px,
    languageId: String = "luau",
    themeId: String = "robloxDark",
    assetId: String? = null,
    assetPrompt: String? = null,
    isPluginConnected: Boolean = false,
    onSyncRequest: () -> Unit = {},
    onFullscreenRequest: (() -> Unit)? = null
) {
    val theme = LocalAppTheme.current
    val s = strings()
    val onCodeChangeState = rememberUpdatedState(onCodeChange)

    var editorRef by remember { mutableStateOf<dynamic>(null) }
    var containerRef by remember { mutableStateOf<HTMLDivElement?>(null) }
    var monacoReady by remember { mutableStateOf(false) }
    var failedToLoadMonaco by remember { mutableStateOf(false) }

    var canUndo by remember { mutableStateOf(false) }
    var canRedo by remember { mutableStateOf(false) }
    var currentCode by remember { mutableStateOf(initialCode) }
    var copied by remember { mutableStateOf(false) }
    var runLoading by remember { mutableStateOf(false) }
    var runSuccess by remember { mutableStateOf<Boolean?>(null) }

    val isCurrentCodeEqualToInitial = currentCode == initialCode

    DisposableEffect(Unit) {
        val win = window.asDynamic()
        val loadedHandler: (dynamic) -> Unit = {
            monacoReady = true
            failedToLoadMonaco = false
        }
        val failedHandler: (dynamic) -> Unit = {
            monacoReady = false
            failedToLoadMonaco = true
        }
        win.addEventListener("monaco-loaded", loadedHandler)
        win.addEventListener("monaco-failed", failedHandler)

        ensureMonacoLoaded(
            onLoaded = {
                monacoReady = true
                failedToLoadMonaco = false
            },
            attempt = 0,
            timeoutStartMs = nowMsEd()
        )

        onDispose {
            win.removeEventListener("monaco-loaded", loadedHandler)
            win.removeEventListener("monaco-failed", failedHandler)
        }
    }

    LaunchedEffect(monacoReady, containerRef) {
        if (!monacoReady) return@LaunchedEffect
        val container = containerRef ?: return@LaunchedEffect
        if (editorRef != null) return@LaunchedEffect

        try {
            val win = window.asDynamic()
            val monaco = win.monaco
            if (monaco == null || monaco == undefined) return@LaunchedEffect

            val createOptions: dynamic = js(
                "(function(v,lang,th,ts,ro){return {value:v,language:lang,theme:th,minimap:{enabled:false},automaticLayout:true,fontSize:14,fontFamily:'ui-monospace, SF Mono, Menlo, Consolas, monospace',wordWrap:'on',tabSize:ts,insertSpaces:true,readOnly:ro,renderLineHighlight:'all',scrollBeyondLastLine:false,bracketPairColorization:{enabled:true},matchBrackets:'always',padding:{top:8,bottom:8},smoothScrolling:true};})"
            )
            val options: dynamic = createOptions(initialCode, languageId, themeId, tabSize, readOnly)

            val editor = monaco.editor.create(container, options)
            editorRef = editor
            currentCode = initialCode

            editor.onDidChangeModelContent { _: Any? ->
                val value = editor.getValue() as String
                currentCode = value
                onCodeChangeState.value(value)

                val model = editor.getModel()
                canUndo = model?.canUndo() as? Boolean ?: false
                canRedo = model?.canRedo() as? Boolean ?: false
            }

            val model = editor.getModel()
            canUndo = model?.canUndo() as? Boolean ?: false
            canRedo = model?.canRedo() as? Boolean ?: false
        } catch (e: Throwable) {
            console.error("[CodeEditor] Monaco create error: ${e.message}")
            failedToLoadMonaco = true
        }
    }

    LaunchedEffect(initialCode, editorRef) {
        val editor = editorRef ?: return@LaunchedEffect
        try {
            val currentVal = editor.getValue() as String
            if (currentVal != initialCode) {
                editor.setValue(initialCode)
                currentCode = initialCode
            }
        } catch (_: Throwable) {
        }
    }

    DisposableEffect(Unit) {
        onDispose {
            try {
                editorRef?.dispose()
            } catch (_: Throwable) {
            }
            editorRef = null
        }
    }

    if (failedToLoadMonaco) {
        CodeViewer(
            code = initialCode,
            language = if (languageId == "luau") "lua" else languageId,
            showLineNumbers = true,
            showCopyButton = true,
            maxHeight = maxHeight,
            modifier = modifier,
            onFullscreenRequest = { onFullscreenRequest?.invoke() }
        )
        return
    }

    val lineCount = remember(currentCode) { currentCode.lines().size }

    Column(modifier = CodeViewerContainerStyle.toModifier().then(modifier)) {
        Row(
            modifier = CodeViewerHeaderStyle.toModifier(),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(0.375.cssRem)) {
                Div(
                    Modifier
                        .fontSize(0.75.cssRem)
                        .fontWeight(FontWeight.Bold)
                        .color(theme.colors.accent)
                        .toAttrs()
                ) {
                    SpanText("🟣 ${if (languageId == "luau") "Luau" else languageId.uppercase()}")
                }
                Div(
                    Modifier
                        .fontSize(0.7.cssRem)
                        .color(theme.colors.textSecondary)
                        .toAttrs()
                ) {
                    SpanText("• ${s.common.lines(lineCount)}")
                }
            }

            Spacer()

            Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(0.375.cssRem)) {
                AppButton(
                    text = "",
                    iconLeft = "↩️",
                    variant = AppButtonVariant.OUTLINE,
                    size = AppButtonSize.SM,
                    enabled = canUndo && !readOnly,
                    onClick = {
                        try {
                            editorRef?.trigger("kb", "undo", null)
                        } catch (_: Throwable) {
                        }
                    },
                    modifier = Modifier.attr("title", s.code.undoTooltip)
                )

                AppButton(
                    text = "",
                    iconLeft = "↪️",
                    variant = AppButtonVariant.OUTLINE,
                    size = AppButtonSize.SM,
                    enabled = canRedo && !readOnly,
                    onClick = {
                        try {
                            editorRef?.trigger("kb", "redo", null)
                        } catch (_: Throwable) {
                        }
                    },
                    modifier = Modifier.attr("title", s.code.redoTooltip)
                )

                AppButton(
                    text = "",
                    iconLeft = "🔄",
                    variant = AppButtonVariant.OUTLINE,
                    size = AppButtonSize.SM,
                    enabled = !isCurrentCodeEqualToInitial && !readOnly,
                    onClick = {
                        try {
                            editorRef?.setValue(initialCode)
                            currentCode = initialCode
                            onCodeChangeState.value(initialCode)
                        } catch (_: Throwable) {
                        }
                    },
                    modifier = Modifier.attr("title", s.code.resetTooltip)
                )

                AppButton(
                    text = if (copied) s.common.copied else "",
                    iconLeft = if (copied) "✅" else "📋",
                    variant = AppButtonVariant.OUTLINE,
                    size = AppButtonSize.SM,
                    onClick = {
                        copyToClipboard(currentCode)
                        copied = true
                        window.setTimeout({ copied = false }, 2000)
                    },
                    modifier = Modifier.attr("title", s.code.copyTooltip)
                )

                AppButton(
                    text = "",
                    iconLeft = "💾",
                    variant = AppButtonVariant.OUTLINE,
                    size = AppButtonSize.SM,
                    onClick = {
                        try {
                            val slug = assetPrompt
                                ?.split(' ')
                                ?.take(10)
                                ?.joinToString("-")
                                ?.lowercase()
                                ?.replace(Regex("[^a-z0-9-]"), "")
                                ?.take(40)
                                ?.ifBlank { null }
                                ?: "script"
                            val shortId = assetId?.take(8) ?: toBase36(nowMsEd()).take(8)
                            val filename = "generated-$slug-$shortId.lua"
                            val content = currentCode
                            val doDownload = js(
                                "(function(c,f){try{var b=new Blob([c],{type:'text/plain;charset=utf-8'});var u=URL.createObjectURL(b);var a=document.createElement('a');a.href=u;a.download=f;document.body.appendChild(a);a.click();document.body.removeChild(a);setTimeout(function(){URL.revokeObjectURL(u);},1000);}catch(e){console.error('[CodeEditor] Download error:',e);}})"
                            )
                            doDownload(content, filename)
                        } catch (e: Throwable) {
                            console.error("[CodeEditor] Download error: ${e.message}")
                        }
                    },
                    modifier = Modifier.attr("title", s.code.downloadTooltip)
                )

                if (onFullscreenRequest != null) {
                    AppButton(
                        text = "",
                        iconLeft = "👁️",
                        variant = AppButtonVariant.OUTLINE,
                        size = AppButtonSize.SM,
                        onClick = onFullscreenRequest,
                        modifier = Modifier.attr("title", s.code.fullscreenTooltip)
                    )
                }

                AppButton(
                    text = when {
                        runLoading -> "..."
                        runSuccess == true -> "✅"
                        runSuccess == false -> "❌"
                        else -> s.code.run
                    },
                    iconLeft = if (runLoading) "⏳" else "▶️",
                    variant = AppButtonVariant.PRIMARY,
                    size = AppButtonSize.SM,
                    enabled = !runLoading && isPluginConnected && !readOnly,
                    onClick = {
                        runLoading = true
                        runSuccess = null
                        try {
                            onSyncRequest()
                            window.setTimeout({
                                runLoading = false
                                runSuccess = true
                                window.setTimeout({ runSuccess = null }, 2000)
                            }, 1500)
                        } catch (_: Throwable) {
                            runLoading = false
                            runSuccess = false
                            window.setTimeout({ runSuccess = null }, 2000)
                        }
                    },
                    modifier = Modifier.attr(
                        "title",
                        if (isPluginConnected) s.code.runViaBackend else s.code.connectPluginFirst
                    )
                )
            }
        }

        Box(
            Modifier
                .fillMaxWidth()
                .minHeight(minHeight)
                .maxHeight(maxHeight)
                .overflow(Overflow.Hidden)
                .backgroundColor(theme.colors.background)
        ) {
            Div(
                attrs = {
                    ref { element ->
                        containerRef = element
                        onDispose { containerRef = null }
                    }
                    style {
                        width(100.percent)
                        height(100.percent)
                        minHeight(minHeight)
                    }
                }
            )
        }
    }
}
